import { Button } from '@/components/ui/Button';

export interface SegmentedFilterOption<T extends string | number> {
  value: T;
  label: string;
}

export type SegmentedFilterVariant = 'segmented' | 'compact';

interface SegmentedFilterProps<T extends string | number> {
  options: readonly SegmentedFilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * `segmented` — gray-container/blue-pill tabs matching the /baltic page
   * "Historical trends" selector.
   * `compact` — slim flex buttons matching the dashboard Baltic widget.
   */
  variant?: SegmentedFilterVariant;
  /** Names the group for screen readers, e.g. "Vessel size". */
  ariaLabel?: string;
  className?: string;
}

/**
 * Presentational two-or-more-way toggle. Extracted from `VesselSizeFilter` so
 * the same control can be driven by the numeric vessel-size ids Fixtures uses
 * and by the 'VL'/'SM' segment strings Cargo uses, without either feature
 * importing the other's encoding.
 *
 * `stopPropagation` is deliberate: these render inside clickable cards.
 */
export function SegmentedFilter<T extends string | number>({
  options,
  value,
  onChange,
  variant = 'segmented',
  ariaLabel,
  className = '',
}: SegmentedFilterProps<T>) {
  if (variant === 'compact') {
    return (
      <div role="group" aria-label={ariaLabel} className={`flex gap-1 ${className}`}>
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            aria-pressed={value === opt.value}
            onClick={(e) => {
              e.stopPropagation();
              onChange(opt.value);
            }}
            className={`flex-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors sm:px-2 sm:py-1 sm:text-xs ${
              value === opt.value
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`grid gap-0.5 rounded-md bg-gray-200 p-0.5 dark:bg-gray-700 sm:gap-1 sm:rounded-lg sm:p-1 ${className}`}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((opt) => (
        <Button
          key={opt.value}
          size="sm"
          variant={value === opt.value ? 'primary' : 'secondary'}
          aria-pressed={value === opt.value}
          onClick={(e) => {
            e.stopPropagation();
            onChange(opt.value);
          }}
          className={`whitespace-nowrap px-2 py-1 text-[11px] sm:text-xs ${
            value === opt.value ? '' : 'bg-transparent hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}
